import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule } from '@angular/router';
import { take } from 'rxjs';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { NotificationType, NotificationService } from '@olv/ui-shared/services/notification.service';
import { TaskNotification } from '@olv/ui-shared/services/signalr.service';
import { ShowMoreComponent } from '../show-more/show-more.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';

export type NotificationTab = 'all' | NotificationType;
export type ConnectionState = 'Connected' | 'Connecting' | 'Reconnecting' | 'Disconnected';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatBadgeModule,
    MatDividerModule,
    RouterModule,
    HasPermissionDirective,
    ShowMoreComponent,
  ],
  templateUrl: './notification.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent implements OnInit {
  private notificationService = inject(NotificationService);

  protected readonly appCtx = inject(AppContextService);
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  @Input() notifications: TaskNotification[] = [];
  @Input() unreadCount = 0;
  @Input() totalNotificationsCount = 0;
  @Input() selectedNotificationTab: NotificationTab = 'all';
  @Input() connectionState!: ConnectionState;
  @Input() hubConnectionState: any;
  @Output() specificNotificationClick = new EventEmitter<TaskNotification>();
  @Output() clearNotifications = new EventEmitter<void>();
  @Output() tabSelect = new EventEmitter<NotificationTab>();
  @Output() showMoreClick = new EventEmitter<void>();

  isSettlementApp = this.appCtx.getAppName() === 'settlement';

  notificationTabs: NotificationTab[] = [
    'all',
    NotificationType.Transactions,
    NotificationType.Wallets,
    NotificationType.Unread,
  ];

  ngOnInit(): void {
    if (this.isSettlementApp) {
      this.notificationTabs.push(NotificationType.Details);
    }
  }

  onSpecificNotificationClick(notification: TaskNotification): void {
    this.specificNotificationClick.emit(notification);
  }

  closeMenu(): void {
    this.menuTrigger.closeMenu();
  }

  onSelectTab(tab: NotificationTab, event?: Event): void {
    event?.stopPropagation();
    if (this.selectedNotificationTab === tab) return;
    this.selectedNotificationTab = tab;
    this.tabSelect.emit(tab);
  }

  get displayNotifications(): TaskNotification[] {
    return this.notifications;
  }

  get hasNotifications(): boolean {
    return this.notifications.length > 0;
  }

  getConnectionClass(): string {
    switch (this.connectionState) {
      case 'Connected':
        return 'connected';
      case 'Connecting':
      case 'Reconnecting':
        return 'connecting';
      case 'Disconnected':
        return 'disconnected';
      default:
        return '';
    }
  }

  getConnectionIcon(): string {
    switch (this.connectionState) {
      case 'Connected':
        return 'notifications';
      case 'Connecting':
      case 'Reconnecting':
        return 'sync';
      case 'Disconnected':
        return 'notifications_off';
      default:
        return 'notifications';
    }
  }
  get hasMoreNotifications(): boolean {
    return this.notifications.length < this.totalNotificationsCount;
  }
  showMore(): void {
    this.showMoreClick.emit();
  }

  formatNotificationTime(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }
  get unreadBadge(): string {
    return this.unreadCount > 99 ? '99+' : `${this.unreadCount}`;
  }

  get hasUnreadNotifications(): boolean {
    return this.notifications.some((n) => !n.isRead);
  }

  labelForTab(tab: NotificationTab): string {
    if (tab === 'all') return 'All';
    switch (tab) {
      case NotificationType.Transactions:
        return 'Transactions';
      case NotificationType.Wallets:
        return 'Wallet';
      case NotificationType.Details:
        return 'Detail updates';
      case NotificationType.Unread:
        return 'Unread';
      default:
        return 'All';
    }
  }
  markAllAsRead(event: Event): void {
    event.stopPropagation();

    const filter = this.selectedNotificationTab === 'all' ? undefined : this.selectedNotificationTab;

    this.notificationService
      .markAllAsRead(filter)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.tabSelect.emit(this.selectedNotificationTab);
        },
        error: (err) => {
          console.error('Failed to mark all notifications as read', err);
        },
      });
  }
}
